// src/components/orders/OrdersSummaryCards.js
import React, { useState, useMemo } from 'react';
import { Card, Row, Col, Statistic, Tag, Space, Divider, Select, Typography } from 'antd';

const { Text } = Typography;
import { 
    ShoppingCartOutlined, 
    DollarOutlined, 
    FileTextOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    TruckOutlined,
    InboxOutlined,
    CalendarOutlined,
    CheckSquareOutlined,
    ExclamationCircleOutlined,
    GiftOutlined,
    AccountBookOutlined
    // TagOutlined: reimportar si se vuelve a mostrar la card "Descuentos Obtenidos"
} from '@ant-design/icons';
import './OrdersSummaryCards.css';

const { Option } = Select;
/**
 * OrdersSummaryCards Component
 * Displays summary statistics for purchase orders with overall and status breakdown
 * Includes monthly filtering for main statistics
 * 
 * Props:
 * - summary: Object containing overall and byStatus summary data from API
 * - supplierName: (Optional) String - If showing for specific supplier
 * - orders: (Optional) Array - Orders data for monthly calculations
 */
const OrdersSummaryCards = ({ summary, supplierName, orders = [], loading = false, isDemoMode = false }) => {
    if (!summary) return null;

    const { overall, byStatus } = summary;
    
    // Get current month/year for default
    const currentDate = new Date();
    const currentMonthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    
    // Shared month selector state for Total Órdenes / Monto Total / IVA Total
    const [selectedMonth, setSelectedMonth] = useState(isDemoMode ? null : currentMonthKey);

    // Independent month selector for Pagado card
    const [selectedPaidMonth, setSelectedPaidMonth] = useState(isDemoMode ? null : currentMonthKey);

    // Format currency
    const formatCurrency = (amount) => {
        if (typeof amount !== 'number' || isNaN(amount)) return '$ 0.00';
        return `$ ${amount.toLocaleString('es-AR', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
        })}`;
    };

    // Status configuration with icons and colors
    const statusConfig = {
        PENDIENTE: { 
            color: 'orange', 
            icon: <ClockCircleOutlined />, 
            label: 'Pendiente' 
        },
        APROBADO: { 
            color: 'blue', 
            icon: <CheckCircleOutlined />, 
            label: 'Aprobado' 
        },
        EN_TRANSITO: { 
            color: 'cyan', 
            icon: <TruckOutlined />, 
            label: 'En Tránsito' 
        },
        PARCIAL: { 
            color: 'purple', 
            icon: <InboxOutlined />, 
            label: 'Parcial' 
        },
        RECIBIDO: { 
            color: 'green', 
            icon: <CheckCircleOutlined />, 
            label: 'Recibido' 
        },
        CANCELADO: { 
            color: 'red', 
            icon: <CloseCircleOutlined />, 
            label: 'Cancelado' 
        },
    };

    // Build available months from the monthlyBreakdown returned by the backend
    const monthlyBreakdown = overall?.monthlyBreakdown || [];

    const availableMonths = useMemo(() => {
        return monthlyBreakdown
            .map(d => `${d.year}-${String(d.month).padStart(2, '0')}`)
            .sort()
            .reverse();
    }, [monthlyBreakdown]);

    // Helper: get monthly breakdown row for a given monthKey
    const getMonthRow = (monthKey) => {
        if (!monthKey) return null;
        const [year, month] = monthKey.split('-').map(Number);
        return monthlyBreakdown.find(d => d.year === year && d.month === month) || null;
    };

    // Get month display name
    const getMonthName = (monthKey) => {
        if (!monthKey) return 'Todos los períodos';
        const [year, month] = monthKey.split('-');
        const monthNames = [
            'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
            'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
        ];
        return `${monthNames[parseInt(month) - 1]} ${year}`;
    };

    const hasMonthlyData = availableMonths.length > 0;

    // Get filtered values for top-3 cards (Total Órdenes / Monto Total / IVA Total)
    // Falls back to overall totals when no monthly breakdown is available (e.g. per-supplier modal)
    const getTopCardData = (monthKey) => {
        if (!monthKey || !hasMonthlyData) {
            return {
                count: overall?.all?.count || 0,
                total: overall?.all?.total || 0,
                iva:   overall?.iva_paid_all || 0,
                net:   overall?.net_total_all || 0,
                discount: overall?.supplier_discount_total || 0,
            };
        }
        const row = getMonthRow(monthKey);
        return row
            ? {
                count: row.count_monthly,
                total: row.total_monthly,
                iva:   row.iva_paid_monthly || 0,
                net:   row.net_total_monthly || 0,
                discount: row.discount_monthly || 0,
              }
            : { count: 0, total: 0, iva: 0, net: 0, discount: 0 };
    };

    // Get Pagado value
    const getPaidValue = (monthKey) => {
        if (!monthKey || !hasMonthlyData) return overall?.paid_total || 0;
        const row = getMonthRow(monthKey);
        return row ? row.paid_monthly : 0;
    };
    const topData = getTopCardData(selectedMonth);

    // Month selector shared for top-3 cards
    const renderMonthSelector = (value, onChange) => (
        hasMonthlyData && (
            <div className="card-month-selector">
                <CalendarOutlined style={{ marginRight: 4, color: '#595959' }} />
                <Select
                    size="small"
                    value={value}
                    onChange={onChange}
                    style={{ width: 130 }}
                    dropdownMatchSelectWidth={false}
                    disabled={isDemoMode}
                >
                    <Option value={null}>Todos</Option>
                    {availableMonths.map(month => (
                        <Option key={month} value={month}>
                            {getMonthName(month)}
                        </Option>
                    ))}
                </Select>
            </div>
        )
    );

    // Get active statuses (those with count > 0)
    const activeStatuses = byStatus 
        ? Object.entries(byStatus)
            .filter(([_, stats]) => stats.count > 0)
            .map(([status, stats]) => ({ status, stats }))
        : [];

    return (
        <div className="orders-summary-cards">
            {supplierName && (
                <div className="summary-supplier-header">
                    <h3>Resumen de Órdenes - {supplierName}</h3>
                </div>
            )}

            {/* 5 Summary Cards — one row, equal width */}
            <Row gutter={[12, 12]} className="summary-five-cards">
                {/* 1. Total de Órdenes */}
                <Col flex="1" style={{ minWidth: 180 }}>
                    <Card size="small" loading={loading} className="summary-five-card" style={{ borderLeft: '4px solid #1890ff' }}>
                        <div className="card-header-row">
                            <div className="card-title-with-icon">
                                <ShoppingCartOutlined className="card-icon" />
                                <span className="card-title">Total de Órdenes</span>
                            </div>
                        </div>
                        {renderMonthSelector(selectedMonth, setSelectedMonth)}
                        <Statistic
                            value={topData.count}
                            valueStyle={{ color: '#1890ff', fontSize: '24px' }}
                        />
                        {selectedMonth && (
                            <Text type="secondary" style={{ fontSize: 13 }}>{getMonthName(selectedMonth)}</Text>
                        )}
                    </Card>
                </Col>

                {/* 2. Monto Total */}
                <Col flex="1" style={{ minWidth: 180 }}>
                    <Card size="small" loading={loading} className="summary-five-card" style={{ borderLeft: '4px solid #722ed1' }}>
                        <div className="card-header-row">
                            <div className="card-title-with-icon">
                                <DollarOutlined className="card-icon" />
                                <span className="card-title">Monto Total</span>
                            </div>
                        </div>
                        {renderMonthSelector(selectedMonth, setSelectedMonth)}
                        <Statistic
                            value={topData.total}
                            precision={0}
                            prefix="$"
                            valueStyle={{ color: '#722ed1', fontSize: '24px' }}
                        />
                        {selectedMonth && (
                            <Text type="secondary" style={{ fontSize: 13 }}>{getMonthName(selectedMonth)}</Text>
                        )}
                    </Card>
                </Col>

                {/* 2b. Descuentos Obtenidos — OCULTA por decision del usuario (27/10/2026).
                      El dato sigue disponible: topData.discount (mes seleccionado) y
                      overall.supplier_discount_total (todo el historico).
                      Para volver a mostrarla: restaurar este bloque, usar
                      value={topData.discount} + renderMonthSelector(selectedMonth, setSelectedMonth)
                      y reimportar TagOutlined en los imports de @ant-design/icons. */}
                {/*
                <Col flex="1" style={{ minWidth: 180 }}>
                    <Card size="small" loading={loading} className="summary-five-card" style={{ borderLeft: '4px solid #52c41a' }}>
                        <div className="card-header-row">
                            <div className="card-title-with-icon">
                                <TagOutlined className="card-icon" />
                                <span className="card-title">Descuentos Obtenidos</span>
                            </div>
                        </div>
                        {renderMonthSelector(selectedMonth, setSelectedMonth)}
                        <Statistic
                            value={topData.discount}
                            precision={2}
                            prefix="$"
                            valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                        />
                        {selectedMonth && (
                            <Text type="secondary" style={{ fontSize: 13 }}>{getMonthName(selectedMonth)}</Text>
                        )}
                    </Card>
                </Col>
                */}
                {/* 2c. Costo Real (neto) */}
                <Col flex="1" style={{ minWidth: 180 }}>
                    <Card size="small" loading={loading} className="summary-five-card" style={{ borderLeft: '4px solid #fa8c16' }}>
                        <div className="card-header-row">
                            <div className="card-title-with-icon">
                                <AccountBookOutlined className="card-icon" />
                                <span className="card-title">Costo Real (neto)</span>
                            </div>
                        </div>
                        {renderMonthSelector(selectedMonth, setSelectedMonth)}
                        <Statistic
                            value={topData.net}
                            precision={2}
                            prefix="$"
                            valueStyle={{ color: '#fa8c16', fontSize: '24px' }}
                        />
                        {selectedMonth && (
                            <Text type="secondary" style={{ fontSize: 13 }}>{getMonthName(selectedMonth)}</Text>
                        )}
                    </Card>
                </Col>

                {/* 3. Pagado — independent month selector */}
                <Col flex="1" style={{ minWidth: 180 }}>
                    <Card size="small" loading={loading} className="summary-five-card" style={{ borderLeft: '4px solid #52c41a' }}>
                        <div className="card-header-row">
                            <div className="card-title-with-icon">
                                <CheckSquareOutlined className="card-icon" style={{ color: '#52c41a' }} />
                                <span className="card-title">Pagado</span>
                            </div>
                        </div>
                        {renderMonthSelector(selectedPaidMonth, setSelectedPaidMonth)}
                        <Statistic
                            value={getPaidValue(selectedPaidMonth)}
                            precision={2}
                            prefix="$"
                            valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                        />
                        {selectedPaidMonth && (
                            <Text type="secondary" style={{ fontSize: 13 }}>{getMonthName(selectedPaidMonth)}</Text>
                        )}
                    </Card>
                </Col>

                {/* 4. IVA Pagado */}
                <Col flex="1" style={{ minWidth: 180 }}>
                    <Card size="small" loading={loading} className="summary-five-card" style={{ borderLeft: '4px solid #d4b106' }}>
                        <div className="card-header-row">
                            <div className="card-title-with-icon">
                                <FileTextOutlined className="card-icon" />
                                <span className="card-title">IVA Pagado</span>
                            </div>
                        </div>
                        {renderMonthSelector(selectedMonth, setSelectedMonth)}
                        <Statistic
                            value={topData.iva}
                            precision={0}
                            prefix="$"
                            valueStyle={{ color: '#d4b106', fontSize: '24px' }}
                        />
                        {selectedMonth && (
                            <Text type="secondary" style={{ fontSize: 13 }}>{getMonthName(selectedMonth)}</Text>
                        )}
                    </Card>
                </Col>

                {/* 5. Pendiente de Pago — always total */}
                <Col flex="1" style={{ minWidth: 200 }}>
                    <Card size="small" loading={loading} className="summary-five-card" style={{ borderLeft: '4px solid #fa541c' }}>
                        <div className="card-header-row">
                            <div className="card-title-with-icon">
                                <ExclamationCircleOutlined className="card-icon" style={{ color: '#fa541c' }} />
                                <span className="card-title">Pendiente de Pago</span>
                            </div>
                        </div>
                        <Statistic
                            value={overall?.pending_total || 0}
                            precision={2}
                            prefix="$"
                            valueStyle={{ color: '#fa541c', fontSize: '24px' }}
                        />
                    </Card>
                </Col>

                {/* 6. Saldo a Favor — only shown when paid > total */}
                {(() => {
                    const saldoAFavor = Math.max(0, overall?.credit_total || 0);
                    if (saldoAFavor <= 0) return null;
                    return (
                        <Col flex="1" style={{ minWidth: 200 }}>
                            <Card size="small" loading={loading} className="summary-five-card" style={{ borderLeft: '4px solid #389e0d', background: '#f6ffed' }}>
                                <div className="card-header-row">
                                    <div className="card-title-with-icon">
                                        <GiftOutlined className="card-icon" style={{ color: '#389e0d' }} />
                                        <span className="card-title" style={{ color: '#389e0d' }}>Saldo a Favor</span>
                                    </div>
                                </div>
                                <Statistic
                                    value={saldoAFavor}
                                    precision={2}
                                    prefix="$"
                                    valueStyle={{ color: '#389e0d', fontSize: '24px' }}
                                />
                            </Card>
                        </Col>
                    );
                })()}
            </Row>

            {/* Status Breakdown */}
            {activeStatuses.length > 0 && (
                <>
                    <Divider orientation="left">Por Estado</Divider>
                    <Row gutter={[12, 12]}>
                        {activeStatuses.map(({ status, stats }) => {
                            const config = statusConfig[status] || { 
                                color: 'default', 
                                icon: <FileTextOutlined />, 
                                label: status 
                            };
                            
                            return (
                                <Col xs={24} sm={12} md={8} lg={4} key={status}>
                                    <Card 
                                        className="summary-card status-card"
                                        size="small"
                                    >
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Tag 
                                                color={config.color} 
                                                icon={config.icon}
                                                style={{ marginBottom: 8 }}
                                            >
                                                {config.label}
                                            </Tag>
                                            <div className="status-stats">
                                                <div className="stat-row">
                                                    <span className="stat-label">Órdenes:</span>
                                                    <span className="stat-value">{stats.count}</span>
                                                </div>
                                                <div className="stat-row">
                                                    <span className="stat-label">Total:</span>
                                                    <span className="stat-value stat-money">
                                                        {formatCurrency(stats.total)}
                                                    </span>
                                                </div>
                                                <div className="stat-row">
                                                    <span className="stat-label">IVA:</span>
                                                    <span className="stat-value stat-money-small">
                                                        {formatCurrency(stats.iva)}
                                                    </span>
                                                </div>
                                            </div>
                                        </Space>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                </>
            )}
        </div>
    );
};

export default OrdersSummaryCards;

